import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Button, Intent, Position} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';
import {Popover2} from '@blueprintjs/popover2';

import {ApiStatus, IUser, SharingSettingsTypes} from '../../../../../models';
import {loadSharingSettings, unsetSharingSettings} from '../../../../../store/actions/sharingSettingsActions';
import {loadUsers} from '../../../../../store/actions/userActions';
import {IState} from '../../../../../store/reducers';
import {useCurrentUser} from '../../../../../hooks';
import ShareReportPopover from './ShareReportPopover';
import {canUserEditResource} from '../../../../../utils/auth';

import './ShareReport.scss';

interface ShareReportProps {
  uid: string;
}

export const ShareReport = ({
  uid,
}: ShareReportProps) => {
  const [icon, setIcon] = useState(IconNames.LOCK);
  const report_ = useSelector((state: IState) => state.reportForm.report);
  const report = report_?.uid === uid ? report_ : null;
  const sharingSettings = useSelector((state: IState) => state.sharingSettings.reportSharingSettings);
  const loadingStatus = useSelector((state: IState) => state.sharingSettings.loadingStatus);
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadUsers({page: 0, page_size: -1, reload: true}));
    dispatch(loadSharingSettings(SharingSettingsTypes.REPORT, uid));

    return () => {
      dispatch(unsetSharingSettings(SharingSettingsTypes.REPORT));
    }
  }, []);

  useEffect(() => {
    if (!sharingSettings) {
      return;
    }
    const sharedWithOtherUsers = sharingSettings
      .shared_with_users
      .filter((u: any) => u.uid !== currentUser?.uid).length > 0;
    const sharedWithOtherGroups = sharingSettings.shared_with_groups.length > 0;

    if (sharingSettings.shared_with_workspace.edit || sharingSettings.shared_with_workspace.view) {
      setIcon(IconNames.OFFICE);
    } else if (sharedWithOtherUsers || sharedWithOtherGroups) {
      setIcon(IconNames.PEOPLE);
    } else {
      setIcon(IconNames.LOCK);
    }
  }, [sharingSettings]);

  if (loadingStatus === ApiStatus.LOADED && !sharingSettings) {
    return null;
  }

  return (
    <Popover2
      content={
        <ShareReportPopover
          uid={uid}
          type={report?.type}
          sharingSettings={sharingSettings as any}
        />
      }
      position={Position.BOTTOM_LEFT}
    >
      <Button
        className="share-report-button"
        icon={icon}
        text="Share"
        title="Share"
        intent={Intent.NONE}
        loading={loadingStatus === ApiStatus.LOADING}
        disabled={!canUserEditResource(currentUser as IUser, sharingSettings)}
      />
    </Popover2>
  );
}

export default ShareReport;
